import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieGrid from "@/components/feature/MovieGrid";
import PlayerArea from "@/pages/detail/components/PlayerArea";
import { useFavorites } from "@/hooks/useFavorites";
import {
  formatDuration,
  formatViews,
  getMovieById,
  getRecommendations,
} from "@/utils/movie";

export default function DetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [notice, setNotice] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 2600);
    return () => clearTimeout(timer);
  }, [notice]);

  if (!movie) {
    return (
      <SiteLayout>
        <Seo
          title="影片不存在 - 免费电影在线观看"
          description="未找到对应的影片信息，返回首页浏览更多免费高清电影资源。"
          keywords="免费电影在线观看,影片不存在,高清电影"
        />
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
            <i className="ri-film-line" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">
            抱歉，未找到这部影片
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            影片可能已下线或链接有误，去首页看看其他热门电影吧
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-5-line" />
            返回首页
          </button>
        </div>
      </SiteLayout>
    );
  }

  const favorite = isFavorite(movie.id);
  const recommendations = getRecommendations(movie, 5);

  const infoRows: { label: string; value: string; icon: string }[] = [
    { label: "导演", value: movie.director, icon: "ri-video-line" },
    { label: "主演", value: movie.cast.join(" / "), icon: "ri-user-star-line" },
    { label: "类型", value: movie.genres.join(" / "), icon: "ri-price-tag-3-line" },
    { label: "地区", value: movie.region, icon: "ri-map-pin-line" },
    { label: "语言", value: movie.language, icon: "ri-translate-2" },
    { label: "年份", value: `${movie.year} 年`, icon: "ri-calendar-line" },
    { label: "片长", value: formatDuration(movie.duration), icon: "ri-time-line" },
    { label: "播放量", value: formatViews(movie.views), icon: "ri-eye-line" },
  ];

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setNotice("影片链接已复制，快分享给好友吧");
    } catch {
      setNotice(`分享链接：${url}`);
    }
  };

  const handleFavorite = () => {
    const nowFavorite = toggleFavorite(movie.id);
    setNotice(nowFavorite ? "已加入收藏，可在本地随时查看" : "已取消收藏");
  };

  return (
    <SiteLayout>
      <Seo
        title={`${movie.title}（${movie.year}）高清在线观看 - 免费电影在线观看`}
        description={`《${movie.title}》是一部${movie.year}年的${movie.genres.join(
          "、"
        )}电影，由${movie.director}执导，${movie.cast.join("、")}主演。${movie.synopsis.slice(
          0,
          60
        )}...`}
        keywords={`${movie.title},${movie.title}在线观看,${movie.genres.join(
          ","
        )},免费电影在线观看,高清电影`}
      />

      <div className="bg-primary-950">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-300">
            <Link to="/" className="hover:text-accent-400 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to={`/category/${movie.genres[0]}`}
              className="hover:text-accent-400 cursor-pointer"
            >
              {movie.genres[0]}电影
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">{movie.title}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6">
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="lg:w-64 lg:shrink-0">
            <div className="w-40 overflow-hidden rounded-lg bg-background-200 sm:w-52 lg:w-full">
              <img
                src={movie.poster}
                alt={`${movie.title} 电影海报`}
                title={`${movie.title} ${movie.year} 免费在线观看`}
                className="aspect-[2/3] w-full object-cover object-top"
              />
            </div>
            <div className="mt-4 flex gap-3 lg:flex-col">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  favorite
                    ? "bg-accent-500 text-primary-950"
                    : "border border-background-300 bg-background-100 text-foreground-800 hover:border-accent-300"
                }`}
              >
                <i className={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                {favorite ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex flex-1 items-center justify-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-primary-950">
                <i className="ri-star-fill" />
                {movie.rating.toFixed(1)} 分
              </span>
              {movie.genres.map((g) => (
                <Link
                  key={g}
                  to={`/category/${g}`}
                  className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                >
                  {g}
                </Link>
              ))}
            </div>
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              {movie.title}
            </h1>
            <p className="mt-2 text-sm italic text-foreground-500">
              「{movie.tagline}」
            </p>

            <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2.5 rounded-lg border border-background-200 bg-background-100 p-5 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex items-start gap-2 text-sm">
                  <i className={`${row.icon} mt-0.5 text-foreground-400`} />
                  <dt className="shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="min-w-0 text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <section className="mt-10">
          <SectionHeading icon="ri-play-circle-line" title="在线观看" subtitle="演示播放器" />
          <PlayerArea movie={movie} onNotice={setNotice} />
        </section>

        <section className="mt-10">
          <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
            剧情简介
          </h2>
          <p className="mt-3 max-w-4xl text-sm leading-relaxed text-foreground-700 md:text-base">
            {movie.synopsis}
          </p>
        </section>

        <section className="mt-10">
          <SectionHeading
            icon="ri-movie-2-line"
            title="相关推荐"
            subtitle="根据类型与导演为您匹配"
          />
          <MovieGrid movies={recommendations} emptyText="暂无相关推荐" />
        </section>

        <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            返回上一页
          </button>
          <Link
            to={`/category/${movie.genres[0]}`}
            className="flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-apps-2-line" />
            更多{movie.genres[0]}电影
          </Link>
        </div>
      </div>

      {notice && (
        <div className="fixed inset-x-0 bottom-8 z-[70] flex justify-center px-4">
          <div className="flex items-center gap-2 rounded-full bg-foreground-950/90 px-5 py-3 text-sm text-background-50 animate-fade-up">
            <i className="ri-information-line text-accent-400" />
            {notice}
          </div>
        </div>
      )}
    </SiteLayout>
  );
}